import { useLocation, useSearchParams } from 'react-router-dom'
import { useFormikContext } from 'formik'
import { Box, Grid, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'

import {
  TextInputWithHelper,
  IMenuItem,
  MediaInput,
  FormTitle,
  FormSubmitButton,
  FormImage,
  HelperWrapper,
  LocaleSelect,
  ScannersAssociationInput,
} from 'src/components'
import { ImpactsAssociationInput, AccountsAssociationInput, CodeInput } from '../inputs'

import { ETranslationNs, IStepForm, EAccountSubscriptionPlans } from 'src/types'
import { IStepFormProps } from '..'
import { useAccount } from 'src/hooks'

import useStyles from '../styles'
import { createRelativePath } from 'src/common/utils'
import {
  DescriptionRichInput,
  DescriptionSimpleInput,
} from 'src/components/products/product-form/fields'
import React from 'react'
import { Locations } from 'src/components/locaions'

interface IStepFormDesktopProps extends IStepFormProps {
  handleChooseMenuItem: (currentItem: IMenuItem) => void
  menuItems: IMenuItem[]
  locale?: string
  handleLocale?: (locale: string) => void
  isDisabled?: boolean
}

const StepFormDesktop = ({
  isDisabled,
  isAddingNew,
  handleChooseMenuItem,
  menuItems,
  locale,
  handleLocale,
}: IStepFormDesktopProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Navbar])
  const { values, handleChange, errors, setFieldValue } = useFormikContext<IStepForm>()
  const classes = useStyles()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const duplicate = searchParams.get('duplicate')
  const { data: account, isLoading: isAccountLoading } = useAccount()

  // const handleDescriptionGenerate = (value: string) => setFieldValue('description', value)

  const breadcrumbs = [
    {
      text: t('steps-header-title.v', { ns: ETranslationNs.Navbar }),
      path: createRelativePath(location),
    },
    { text: isAddingNew ? 'steps-header-title-add.v' : values.name },
  ]

  const freeAccount = account?.subscription_plan === EAccountSubscriptionPlans.free

  React.useEffect(() => {
    if (duplicate) {
      setFieldValue('name', `${values.name} - ${t('copy-duplicate-postfix.v')}`)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <FormTitle
        menuItems={menuItems}
        handleChooseMenuItem={handleChooseMenuItem}
        breadcrumbs={breadcrumbs}
        setFieldValue={setFieldValue}
        title={values.name}
        imageComponent={<FormImage alt={values.name} />}
        // afterBreadcrumbsNode={
        //   locale && handleLocale ? (
        //     <LocaleSelect locale={locale} handleLocale={handleLocale} />
        //   ) : null
        // }
      />

      <Grid className={classes.form}>
        <Grid className={classes.formWrapper} container>
          {locale && handleLocale && (
            <Box
              sx={{
                px: '5px',
                alignSelf: 'start',
              }}
            >
              <LocaleSelect locale={locale} handleLocale={handleLocale} />
            </Box>
          )}
          <Grid className={classes.codeInputWrapper} container>
            <Grid className={classes.upperInputWrapper} container item>
              <TextInputWithHelper
                disabled={isDisabled}
                inputName="name"
                handleChange={handleChange}
                value={values.name}
                error={errors.name ? t(errors.name + '.v') : ''}
                inputPlaceholder={t('steps-form-name-input-placeholder.v')}
                inputUpperLabel={
                  <HelperWrapper
                    t={'steps-form-name-input-label'}
                    ns={ETranslationNs.Common}
                    isInputLable
                    isMandatory
                  />
                }
              />
              <CodeInput isDisabled={isDisabled} />
            </Grid>
          </Grid>
          <Grid className={classes.descriptionWrapper}>
            {isAccountLoading ? (
              <Skeleton variant="rectangular" height={230} width="100%" />
            ) : freeAccount ? (
              <DescriptionSimpleInput
                isRequired={false}
                isDisabled={isDisabled}
                locale={locale}
                textPrompt={`${t('steps-form-ai-prompt.v')} ${values.name || '{step name}'} ${t(
                  'products-form-ai-prompt-1.v',
                )} ${account?.account_holder || '{account name}'} ${t(
                  'products-form-ai-prompt-2.v',
                )} ${account?.region || '{account region}'} ${
                  account?.country || '{account country}'
                }`}
                generationType="Step"
              />
            ) : (
              <DescriptionRichInput
                isRequired={false}
                isDisabled={isDisabled}
                locale={locale}
                textPrompt={`${t('steps-form-ai-prompt.v')} ${values.name || '{step name}'} ${t(
                  'products-form-ai-prompt-1.v',
                )} ${account?.account_holder || '{account name}'} ${t(
                  'products-form-ai-prompt-2.v',
                )} ${account?.region || '{account region}'} ${
                  account?.country || '{account country}'
                }`}
                generationType="Step"
              />
            )}
          </Grid>

          <Locations
            isDisabled={isDisabled}
            errors={errors}
            locations={values.locations}
            setFieldValue={setFieldValue}
          />

          <MediaInput
            disabled={isDisabled}
            inputName="media"
            media={values.media}
            setFieldValue={setFieldValue}
            mainImageSwitch
            withDnd
          />

          <ImpactsAssociationInput isDisabled={isDisabled} />

          <ScannersAssociationInput<IStepForm>
            description={
              <HelperWrapper t="scanners-association-title" isColorInherited size={12} />
            }
            isDisabled={isDisabled}
          />

          <AccountsAssociationInput isDisabled={isDisabled} />
        </Grid>
        {isAddingNew && !isDisabled && (
          <FormSubmitButton
            notDisabledIfDirty={!!duplicate}
            withoutIcon
            validText={<HelperWrapper t="save-button-text" />}
            invalidText={<HelperWrapper t="save-button-text" />}
          />
        )}
        {!isAddingNew && !isDisabled && (
          <FormSubmitButton
            validText={<HelperWrapper t="save-changes-button-text" ns={ETranslationNs.Form} />}
            invalidText={<HelperWrapper t="steps-form-submit-button-edit" />}
          />
        )}
      </Grid>
    </>
  )
}

export { type IStepFormProps, StepFormDesktop }
